import { useState, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * Square sync operations — trigger sync, check status, fetch catalog.
 */
export function useSquareSync() {
  const [syncing, setSyncing] = useState(false)
  const [catalogLoading, setCatalogLoading] = useState(false)

  const syncEvent = useCallback(async (marketEventId) => {
    setSyncing(true)
    try {
      const { data, error } = await supabase.functions.invoke('square-market-sync', {
        body: {
          action: 'sync-event',
          market_event_id: marketEventId,
          triggered_by: 'manual'
        }
      })

      if (error) throw error

      if (data?.success) {
        const msg = `${data.sales_created} sales created from ${data.orders_synced} orders`
        if (data.items_unmapped > 0) {
          // Strip Square IDs from unmapped item names
          const itemNames = (data.unmapped_items || []).map(name =>
            name.replace(/\s*\([A-Z0-9]+\)$/, '')
          )
          const uniqueNames = [...new Set(itemNames)]
          toast.warning(`Sync complete — ${msg}`, {
            description: `${uniqueNames.length} unmapped products: ${uniqueNames.join(', ')}. Map these in Settings to include them.`
          })
        } else {
          toast.success(`Sync complete — ${msg}`)
        }
      } else {
        toast.error('Sync failed', { description: data?.message || 'Unknown error' })
      }

      return data
    } catch (err) {
      toast.error('Sync failed', { description: err.message })
      throw err
    } finally {
      setSyncing(false)
    }
  }, [])

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('square-market-sync', {
        body: { action: 'catalog' }
      })

      if (error) throw error
      return data?.catalog || []
    } catch (err) {
      toast.error('Failed to fetch Square catalog', { description: err.message })
      throw err
    } finally {
      setCatalogLoading(false)
    }
  }, [])

  const getConnectionStatus = useCallback(async () => {
    const { data, error } = await supabase
      .from('square_sync_config')
      .select('id, merchant_id, location_id, location_name, is_active, token_expires_at, created_at')
      .eq('is_active', true)
      .maybeSingle()

    if (error) {
      console.error('Failed to fetch Square config:', error)
      return null
    }
    return data
  }, [])

  const checkPayouts = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('square-market-sync', {
        body: { action: 'check-payouts' }
      })
      if (error) throw error
      if (data?.checked > 0) {
        toast.success(`Checked ${data.checked} market payouts`)
      } else {
        toast.info('No pending payout checks')
      }
      return data
    } catch (err) {
      toast.error('Payout check failed', { description: err.message })
      throw err
    }
  }, [])

  return {
    syncEvent,
    checkPayouts,
    syncing,
    fetchCatalog,
    catalogLoading,
    getConnectionStatus
  }
}
